import type { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type { AtAGlanceRequestCallbacks } from './atAGlanceTypes.ts';
import {
	getAtAGlanceAbortedEvent,
	getAtAGlanceFailedEvent,
	getAtAGlanceSucceededEvent,
} from './atAGlanceAnalytics.ts';

const reportSafely = (report: () => void) => {
	try {
		report();
	} catch {
		// https://hello.atlassian.net/browse/CCINT-9575: failed telemetry must not change summary completion or retry behavior.
	}
};

export const createAtAGlanceRequestReporter = (
	createEvent: ReturnType<typeof useConfluenceAnalyticsEvents>['createEvent'],
	{
		incomingLinkCount,
		outgoingLinkCount,
	}: { incomingLinkCount: number; outgoingLinkCount: number },
): Pick<AtAGlanceRequestCallbacks, 'onSuccess' | 'onFailure' | 'onAborted'> => ({
	onSuccess: (parsed, attributes) => {
		reportSafely(() =>
			createEvent(
				getAtAGlanceSucceededEvent({
					overviewCount: parsed.overview.length,
					summariesCount: parsed.summaries.length,
					incomingLinkCount,
					outgoingLinkCount,
					...attributes,
				}),
			).fire(),
		);
	},
	onFailure: (failure, attributes) => {
		reportSafely(() =>
			getMonitoringClient().submitError(failure, { attribution: Attribution.INTEGRATIONS }),
		);
		reportSafely(() =>
			createEvent(
				getAtAGlanceFailedEvent({ incomingLinkCount, outgoingLinkCount, ...attributes }),
			).fire(),
		);
	},
	onAborted: (attributes) => {
		reportSafely(() =>
			createEvent(
				getAtAGlanceAbortedEvent({ incomingLinkCount, outgoingLinkCount, ...attributes }),
			).fire(),
		);
	},
});
